import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Button, Col, Container, Row, Spinner, Stack } from "react-bootstrap";
import { currentUrl, unsavedChanges } from "../../../App";
import { fetchFacilities, fetchRooms, postRoom, patchRoom, fetchBeds, postBed } from "../../../services/httpClient";
import PageTitle from "../../ui/PageTitle";
import SelectObject from "../../ui/SelectObject";
import useApp from "../../../utils/useApp";
import FacilityManagementCard from "./FacilityManagementCard";
import RoomManagementCard from "./RoomManagementCard";
import BedManagement from "./BedManagement";
import { bedsOfRoom, propertyCounts, roomsOfProperty } from "../../../helpers/housingAssignment";
import BulkPropertySetupForm from "./BulkPropertySetupForm";

interface FacilityManagementState {
  getData: boolean;
  setOrgToSave: any;
  setFacilityUpdated: any;
  setRoomsUpdated: any;
  setShowToast: any;
  setToastText: any;
  setToastBody: any;
}

const OrgFacilityManagement = ({
  getData = false,
  setOrgToSave,
  setFacilityUpdated,
  setRoomsUpdated,
  setShowToast,
  setToastText,
  setToastBody,
}: FacilityManagementState) => {
  const location = useLocation();
  currentUrl.value = location.pathname;
  const { auth }: any = useApp();
  const [errMsg, setErrMsg] = useState("");
  const [facilities, setFacilities] = useState<any[]>([]);
  const [selectedFacility, setSelectedFacility] = useState<any>(null);
  const [rooms, setRooms] = useState<any[]>([]);
  const [selectedRoom, setSelectedRoom] = useState<any>(null);
  const [unassignedRooms, setUnassignedRooms] = useState<any[]>([]);
  const [pickedRoomKey, setPickedRoomKey] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);
  const [showAddFacility, setShowAddFacility] = useState(false);

  useEffect(() => {
    if (auth?.profile.includeHMIS === 1) {
      getFacilities();
      setFacilityUpdated(false);
    }
  }, [auth?.profile]);

  useEffect(() => {
    if (getData) {
      getFacilities();
      setFacilityUpdated(false);
    }
  }, [getData]);

  const getFacilities = () => {
    fetchFacilities()
      .then((res: any) => {
        const facilityList = res[0].data;
        return fetchRooms().then((roomRes: any) => {
          const roomList = roomRes[0].data;
          return fetchBeds().then((bedRes: any) => {
            const bedList = bedRes[0].data;
            const withCounts = facilityList.map((f: any) => {
              const { roomCount, bedCount } = propertyCounts(roomList, bedList, f.id);
              return { ...f, room_count: roomCount, bed_count: bedCount };
            });
            withCounts.sort((a: any, b: any) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" }));
            setFacilities(withCounts);
          });
        });
      })
      .catch((err: any) => {
        console.log(err);
      })
      .finally(() => {
        setIsLoading(false);
        setErrMsg("");
      });
  };

  const getRoomsForFacility = (facilityId: any) => {
    fetchRooms()
      .then((res: any) => {
        const roomList = res[0].data;
        setUnassignedRooms(roomList.filter((r: any) => !r.property_id));

        return fetchBeds().then((bedRes: any) => {
          const bedList = bedRes[0].data;
          const scopedRooms = roomsOfProperty(roomList, facilityId).map((r: any) => {
            const roomBeds = bedsOfRoom(bedList, r.id);
            return { ...r, bed_count: `${roomBeds.length} beds`, beds: roomBeds };
          });
          scopedRooms.sort((a: any, b: any) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" }));
          setRooms(scopedRooms);
          setSelectedRoom((current: any) => (current ? scopedRooms.find((r: any) => r.id === current.id) || null : null));
        });
      })
      .catch((err: any) => {
        console.log(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const onClickManageRooms = (facility: any) => {
    setIsLoading(true);
    setSelectedFacility(facility);
    getRoomsForFacility(facility.id);
  };

  const onClickBackToFacilities = () => {
    setSelectedFacility(null);
    setRooms([]);
    setSelectedRoom(null);
    setUnassignedRooms([]);
    setPickedRoomKey("");
    getFacilities();
  };

  const onClickAddFacility = () => {
    setErrMsg("");
    setShowAddFacility(true);
  };

  const onFacilityCreated = (facilityName: string) => {
    setShowAddFacility(false);
    setShowToast(true);
    setToastText("Facility Created");
    setToastBody(facilityName);
    setOrgToSave(false);
    getFacilities();
  };

  const onCancelAddFacility = () => {
    setShowAddFacility(false);
    setOrgToSave(false);
  };

  const onClickAddRoom = () => {
    const roomName = rooms?.length ? `Room ${rooms?.length + 1}` : "Room 1";
    postRoom({ name: roomName, property_id: selectedFacility?.id })
      .then(() => {
        setShowToast(true);
        setToastText("Room Created");
        setToastBody(roomName);
        unsavedChanges.value = false;
        setOrgToSave(false);
        getRoomsForFacility(selectedFacility?.id);
      })
      .catch((err: any) => setErrMsg(err?.response?.data?.msg));
  };

  const onUpdatePickedRoom = (e: any) => {
    setPickedRoomKey(e.target.value);
  };

  const onClickAssignRoom = () => {
    const room = unassignedRooms.find((r: any) => r.key === pickedRoomKey);
    if (!room) return;
    setErrMsg("");
    patchRoom({ property_id: selectedFacility?.id }, room.id)
      .then(() => {
        setShowToast(true);
        setToastText("Room Added");
        setToastBody(room.name);
        setPickedRoomKey("");
        setRoomsUpdated(true);
        getRoomsForFacility(selectedFacility?.id);
      })
      .catch((err: any) => setErrMsg(err?.response?.data?.msg));
  };

  const onClickAddBed = () => {
    setErrMsg("");
    const roomId = selectedRoom?.id;
    const bedName = selectedRoom?.beds?.length ? `Bed ${selectedRoom?.beds?.length + 1}` : "Bed 1";
    postBed({ room_id: roomId, name: bedName })
      .then(() => {
        setShowToast(true);
        setToastText("Bed Created");
        setToastBody(bedName);
        unsavedChanges.value = false;
        setOrgToSave(false);
        getRoomsForFacility(selectedFacility?.id);
      })
      .catch((err: any) => setErrMsg(err?.response?.data?.msg));
  };

  const getRoomsRefresh = () => getRoomsForFacility(selectedFacility?.id);

  return (
    <>
      {isLoading ? (
        <Container>
          <Button size="sm" className="spinner-button spinner-button-main">
            <Spinner animation="border" style={{ color: "#F6893D" }} />
          </Button>
        </Container>
      ) : (
        <>
          <p className={errMsg ? "errmsg" : "offscreen"} aria-live="assertive">
            {errMsg}
          </p>
          <PageTitle
            title={selectedRoom ? selectedRoom.name : selectedFacility ? selectedFacility.name : "Facilities"}
            type="section"
            subTitleText={!selectedFacility && !showAddFacility ? "On-site shelter buildings made up of rooms." : undefined}
            buttonTitle={showAddFacility ? undefined : selectedRoom ? "Add Bed" : selectedFacility ? "Add Room" : "Add Facility"}
            buttonColor={"primary"}
            onButtonClick={
              showAddFacility
                ? undefined
                : selectedRoom
                  ? () => onClickAddBed()
                  : selectedFacility
                    ? () => onClickAddRoom()
                    : () => onClickAddFacility()
            }
            backButtonText={
              showAddFacility
                ? "Back to Facilities"
                : selectedRoom
                  ? `Back to ${selectedFacility?.name}`
                  : selectedFacility
                    ? "Back to Facilities"
                    : undefined
            }
            onBackClick={
              showAddFacility
                ? () => onCancelAddFacility()
                : selectedRoom
                  ? () => setSelectedRoom(null)
                  : selectedFacility
                    ? () => onClickBackToFacilities()
                    : null
            }
          />

          {showAddFacility ? (
            <BulkPropertySetupForm
              kind="facility"
              existingNames={facilities.map((f: any) => f.name)}
              onCreated={onFacilityCreated}
              onCancel={onCancelAddFacility}
              setErrMessage={setErrMsg}
            />
          ) : selectedRoom ? (
            <BedManagement
              getItems={getRoomsRefresh}
              beds={selectedRoom?.beds || []}
              setOrgToSave={setOrgToSave}
              setErrMessage={setErrMsg}
              setShowToast={setShowToast}
              setToastText={setToastText}
              setToastBody={setToastBody}
            />
          ) : selectedFacility ? (
            <>
              {unassignedRooms.length > 0 && (
                <Row className="mt-3 align-items-center">
                  <Col xs={12} md={6} lg={5}>
                    <SelectObject
                      label="Add an existing standalone room"
                      value={pickedRoomKey}
                      options={unassignedRooms}
                      onUpdate={(e: any) => onUpdatePickedRoom(e)}
                    />
                  </Col>
                  <Col xs={12} md={4} lg={3}>
                    <Button className="app-secondary-color button" onClick={onClickAssignRoom} disabled={!pickedRoomKey}>
                      Add to Facility
                    </Button>
                  </Col>
                </Row>
              )}
              <Row className="mt-3">
                <Col xs={12} lg={10}>
                  {rooms.length === 0 ? (
                    <p className="mt-2">No Rooms Created</p>
                  ) : (
                    <Stack gap={2}>
                      {rooms?.map((room: any, index: number) => (
                        <Row key={index}>
                          <Col xs={12}>
                            <RoomManagementCard
                              room={room}
                              getItems={getRoomsRefresh}
                              setSelectedRoom={setSelectedRoom}
                              setOrgToSave={setOrgToSave}
                              setErrMessage={setErrMsg}
                              setShowToast={setShowToast}
                              setToastText={setToastText}
                              setToastBody={setToastBody}
                            />
                          </Col>
                        </Row>
                      ))}
                    </Stack>
                  )}
                </Col>
              </Row>
            </>
          ) : (
            <Row className="mt-3">
              <Col xs={12} lg={10}>
                {facilities.length === 0 ? (
                  <p className="mt-2">No Facilities Created</p>
                ) : (
                  <Stack gap={2}>
                    {facilities?.map((facility: any, index: number) => (
                      <Row key={index}>
                        <Col xs={12}>
                          <FacilityManagementCard
                            facility={facility}
                            getItems={getFacilities}
                            onManageRooms={onClickManageRooms}
                            setOrgToSave={setOrgToSave}
                            setErrMessage={setErrMsg}
                            setShowToast={setShowToast}
                            setToastText={setToastText}
                            setToastBody={setToastBody}
                          />
                        </Col>
                      </Row>
                    ))}
                  </Stack>
                )}
              </Col>
            </Row>
          )}

          <Row className="mb-5"></Row>
        </>
      )}
    </>
  );
};

export default React.memo(OrgFacilityManagement);
